/*
 * Business Units Service
 *
 * @author Atanas Kovachev
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */
import {
  HttpClient,
  HttpParams
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Constants } from 'app/shared/config/constants';
import {
  Observable,
  of
} from 'rxjs';
import {
  BusinessUnit,
  BusinessUnitResponse,
  BusinessUnitTreeViewPreload
} from '../models/business-unit.model';
import { UserService } from './user.service';
import { map } from 'rxjs/operators';

@Injectable ( {
  providedIn: 'root',
} )
export class BusinessUnitsService {
  readonly businessUnitsUrl = Constants.businessUnitsUrl;
  readonly userStartingLocationsUrl = Constants.startingLocationsUrl;
  readonly businessUnitAncestorsUrl = Constants.businessUnitAncestorsUrl;

  constructor ( private http: HttpClient, private userService: UserService ) {
  }

  get clientNumber (): string {
    return this.userService.currentClient?.clientNumber;
  }

  /**
   * Provides Starting Locations for the current client
   * @returns Observable of array of business-units
   */
  getStartingLocations (): Observable<BusinessUnit[]> {
    if ( this.clientNumber ) {
      const url = `${ this.userStartingLocationsUrl.replace ( '{clientNumber}', this.clientNumber ) }`;
      return this.http.get<BusinessUnit[]> ( url );
    }
    return of ( [] );
  }

  /**
   * Provides business-unit children
   *
   * @param businessUnitId
   * @param reportingCapable
   * @param searchTerm
   * @param pageNumber
   * @param pageSize
   *
   * @param activeDate
   * @returns Observable of array of business-unit children
   */
  getBusinessUnitChildren (
    businessUnitId: string,
    reportingCapable = false,
    searchTerm = '',
    pageNumber = 1,
    pageSize = 50,
    activeDate?: string ): Observable<BusinessUnitResponse> {

    let params = new HttpParams ()
      .append ( 'pageNumber', `${ pageNumber }` )
      .append ( 'pageSize', `${ pageSize }` )
      .append ( 'activeDate', activeDate || '' )
      .append ( 'reportingCapable', `${ reportingCapable }` );

    if ( searchTerm ) {
      params = params.append ( 'searchTerm', searchTerm );
    }

    if ( this.clientNumber ) {
      const url = this.businessUnitsUrl.replace ( '{clientNumber}', this.clientNumber )
        .replace ( '{businessUnitId}', businessUnitId );
      return this.http.get<BusinessUnitResponse> ( `${ url }/children`, { params } );
    } else {
      return of ( {
        records: [],
        totalRecords: 0,
        currentPage: 0,
        totalPages: 0
      } );
    }
  }

  getBUDetails ( businessUnitId: string ): Observable<any> {
    if ( this.clientNumber ) {
      const url = this.businessUnitsUrl.replace ( '{clientNumber}', this.clientNumber )
        .replace ( '{businessUnitId}', businessUnitId );
      return this.http.get<BusinessUnitResponse> ( url );
    } else {
      return of ( {} );
    }
  }

  getBUAncestors ( businessUnitId: string ): Observable<BusinessUnitTreeViewPreload> {
    if ( this.clientNumber ) {
      const url = this.businessUnitAncestorsUrl.replace ( '{businessUnitId}', businessUnitId );
      return this.http.get<any> ( url )
        .pipe ( map ( result => {
          result.businessUnitAncestry.sort ( ( a, b ) => {
            return a.gbLevel - b.gbLevel;
          } );
          return result;
        } ) );
    } else {
      return of ( {
        businessUnitAncestry: [],
        selectedBusinessUnit: {}
      } as BusinessUnitTreeViewPreload );
    }
  }

  businessUnitsSearch (
    searchCriteria: any,
    pageNumber = 1,
    pageSize = 50 ): Observable<BusinessUnitResponse> {
    let params = new HttpParams ()
      .append ( 'pageNumber', `${ pageNumber }` )
      .append ( 'pageSize', `${ pageSize }` );
    if ( searchCriteria ) {
      for ( const [ key, value ] of Object.entries ( searchCriteria ) ) {
        if ( value ) {
          params = params.append ( key, `${ value }` );
        }
      }
    }
    if ( this.clientNumber ) {
      const url = this.businessUnitsUrl.replace ( '{clientNumber}', this.clientNumber )
        .replace ( '/{businessUnitId}', '' );
      return this.http.get<BusinessUnitResponse> ( `${ url }`, { params } );
    } else {
      return of ( {
        records: [],
        totalRecords: 0,
        currentPage: 0,
        totalPages: 0
      } );
    }
  }

  getBusinessUnitSettings ( businessUnitId: string ): Observable<any[]> {
    const url = this.businessUnitsUrl.replace ( '{clientNumber}', 'any' )
      .replace ( '{businessUnitId}', businessUnitId ) + '/settings';
    return this.http.get<any[]> ( url );
  }
}
